/**
 * Utilidades para POST /api/clienteresumen (respuesta { status, data: { items, total, ... } }).
 */
import axios from "axios";
import { API_URLS_BOARD } from "@/constants";

/**
 * @param {object} body - Normalmente res.data de axios
 * @returns {object[]} filas crudas del API (items o fallbacks legacy)
 */
/** @param {object} row */
function pickNombreDisplay(row) {
  const v = row.nombre ?? row.nombre_cliente ?? '';
  return String(v || '').trim();
}

/** Razón social solo desde campos de razón social (no usar RUT como sustituto en el texto). */
function pickRazonSocialDisplay(row) {
  const v =
    row.facturacion_nombre ??
    row.facturacion_razon_social ??
    row.facturacionRazonSocial ??
    row.razon_social ??
    '';
  return String(v || '').trim();
}

/** Username/login para el tercer segmento (sin repetir nombre si falta username). */
function pickUsernameDisplay(row) {
  const v = row.username ?? row.usuario ?? row.login ?? '';
  return String(v || '').trim();
}

export function extractClienteResumenItems(body) {
  if (!body || typeof body !== 'object') return [];
  const envelope = body.data;
  if (envelope && Array.isArray(envelope.items)) return envelope.items;
  const orig = body.original;
  return (
    body.clientes ||
    (Array.isArray(envelope) ? envelope : envelope?.data) ||
    (orig && (orig.items || orig.data || orig.clientes)) ||
    []
  );
}

/**
 * @param {object} row - Ítem API (cliente_id, nombre, facturacion_nombre, …)
 * @param {{ displayText?: (e: object) => string }} options - Si no hay displayText, text = "nombre - facturacion_nombre - username"
 * @returns {object|null}
 */
export function mapClienteResumenItem(row, options = {}) {
  if (!row || row.cliente_id == null) return null;
  const id =
    typeof row.cliente_id === 'string'
      ? parseInt(row.cliente_id, 10)
      : Number(row.cliente_id);
  const username = row.username || row.nombre || '';
  const facturacion_nombre =
    row.facturacion_nombre ||
    row.facturacion_rut ||
    row.rut ||
    '';
  const nombre = row.nombre || username;
  const normalized = {
    ...row,
    id: Number.isFinite(id) ? id : row.cliente_id,
    username,
    nombre,
    facturacion_nombre,
    // Estado del servicio que coincidió con el dato buscado (IP/dominio):
    // 1 habilitado, 2 suspendido, 0 pendiente, 3 eliminado, null si no aplica.
    match_estado:
      row.match_estado != null && row.match_estado !== ''
        ? Number(row.match_estado)
        : null,
    notificaciones_activadas:
      row.notificaciones_activadas != null ? row.notificaciones_activadas : 1,
  };
  const { displayText } = options;
  if (typeof displayText === 'function') {
    normalized.text = displayText(normalized);
  } else {
    const parts = [
      pickNombreDisplay(row),
      pickRazonSocialDisplay(row),
      pickUsernameDisplay(row),
    ].filter((p) => p.length > 0);
    normalized.text = parts.join(' - ') || String(row.cliente_id);
  }
  return normalized;
}

/**
 * Normaliza el término de búsqueda para POST /api/clienteresumen.
 * El flujo legacy enviaba el segmento de URL "null" cuando no había texto.
 * @param {unknown} search
 * @returns {string}
 */
export function normalizeClienteResumenSearch(search) {
  if (search == null) return '';
  const s = String(search).trim();
  if (!s || s.toLowerCase() === 'null') return '';
  return s;
}

/**
 * Cuerpo estándar para POST /api/clienteresumen con filtro opcional por servicio.
 * @param {{ search?: unknown, page?: number, perPage?: number, id_cliente_servicio?: unknown }} params
 * @returns {{ page: number, perPage: number, search: string } & Record<string, unknown>}
 */
export function buildClienteResumenRequestBody(params = {}) {
  const { search, page = 1, perPage = 30, id_cliente_servicio } = params;
  const body = {
    page,
    perPage,
    search: normalizeClienteResumenSearch(search),
  };
  if (
    id_cliente_servicio != null &&
    id_cliente_servicio !== '' &&
    String(id_cliente_servicio).toLowerCase() !== 'null'
  ) {
    const n = Number(id_cliente_servicio);
    body.id_cliente_servicio = Number.isFinite(n) ? n : id_cliente_servicio;
  }
  return body;
}

/**
 * Consulta genérica de clientes usando endpoint estándar y mapeo homogéneo.
 * @param {{
 *  search?: unknown,
 *  page?: number,
 *  perPage?: number,
 *  id_cliente_servicio?: unknown,
 *  displayText?: (e: object) => string,
 *  extraBody?: Record<string, unknown>,
 *  httpClient?: { post: Function },
 *  requestConfig?: object
 * }} params
 * @returns {Promise<object[]>}
 */
export async function fetchClienteResumenList(params = {}) {
  const {
    search,
    page = 1,
    perPage = 30,
    id_cliente_servicio,
    displayText,
    extraBody = {},
    httpClient = axios,
    requestConfig,
  } = params;

  const body = {
    ...buildClienteResumenRequestBody({
      search,
      page,
      perPage,
      id_cliente_servicio,
    }),
    ...(extraBody && typeof extraBody === "object" ? extraBody : {}),
  };

  const url = `${API_URLS_BOARD.baseUrl}${API_URLS_BOARD.clienteResumen}`;
  const res = await httpClient.post(url, body, requestConfig);
  const rawItems = extractClienteResumenItems(res?.data || {});
  return rawItems
    .map((row) => mapClienteResumenItem(row, { displayText }))
    .filter(Boolean);
}

/**
 * Parsea una fecha en cualquiera de los formatos que devuelven los distintos
 * endpoints consumidos por el resumen de cliente: epoch en segundos,
 * "dd/mm/yyyy" (con hora opcional) o ISO 8601.
 * @param {unknown} v
 * @returns {Date|null}
 */
export function parseFlexibleDate(v) {
  if (!v) return null;
  const str = String(v).trim();
  if (/^\d+$/.test(str)) return new Date(Number(str) * 1000);
  const m = str.match(/^(\d{2})\/(\d{2})\/(\d{4})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?$/);
  if (m) {
    const [, d, mo, y, h = '0', mi = '0', s = '0'] = m;
    const dt = new Date(+y, +mo - 1, +d, +h, +mi, +s);
    return isNaN(dt.getTime()) ? null : dt;
  }
  const dt = new Date(str);
  return isNaN(dt.getTime()) ? null : dt;
}

/**
 * Ordena una lista de registros por un campo de fecha, del más nuevo al más
 * antiguo. Los registros sin fecha válida quedan al final, en el orden
 * recibido.
 * @param {object[]} list
 * @param {string} field - Nombre del campo de fecha (ej. "fecha_emision")
 * @returns {object[]} nueva lista ordenada (no muta la original)
 */
export function sortByDateDesc(list, field) {
  if (!Array.isArray(list)) return [];
  return [...list].sort((a, b) => {
    const ta = parseFlexibleDate(a?.[field]);
    const tb = parseFlexibleDate(b?.[field]);
    if (!ta && !tb) return 0;
    if (!ta) return 1;
    if (!tb) return -1;
    return tb.getTime() - ta.getTime();
  });
}
